import { Link } from "react-router-dom";
import { Truck, Wrench, Users, Layers } from "lucide-react";
import { PlankSymbol } from "../components/Logo";
import { FlowImage } from "../components/FlowImage";
import { Seo, breadcrumbLd } from "../components/Seo";

const IMG = "https://static.prod-images.emergentagent.com/jobs/b9979ec9-3304-4896-8d2c-2eeafd09334a/images/31e3cb392b0c31ac37d035848455c059235a36700fb9806b1d43ca2ded611dd0.jpeg";

export default function Story() {
  return (
    <div data-testid="story-page">
      <Seo
        title="About H&W Furniture – Hyderabad's DIY Modular Furniture Brand"
        description="H&W stands for Husband & Wife. A Hyderabad brand making DIY modular furniture: flat-pack bookshelves you build together, 24 Hrs free delivery, no carpenter needed."
        path="/story"
        jsonLd={[breadcrumbLd([["Home", "/"], ["Our story", "/story"]])]}
      />
      <section className="container-hw grid gap-12 py-10 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-6">
          <p className="eyebrow rise">Our story</p>
          <h1 className="t-display rise d1 mt-4">H&amp;W stands for Husband &amp; Wife.</h1>
          <div className="t-lead rise d2 mt-8 space-y-5">
            <p>We started as two people in a 2BHK in Kondapur with too many books and nowhere to put them. Everything we liked was either flimsy, overpriced, or too big to get into our building's lift.</p>
            <p>So we designed a rack that came flat, went together with one hex key, and could take another shelf later. We built it on the floor over chai. Then friends across Hyderabad asked for one.</p>
            <p>That rack became the Pustak Rack, and H&amp;W became a do-it-yourself modular furniture company. We are Hyderabad-only for now: we deliver across the city ourselves within 24 hours, and we sell direct, so the price you see is honest.</p>
          </div>
          <Link to="/product/pustak-rack" data-testid="story-shop-button" className="btn-sheesham mt-10">Start with the Pustak Rack</Link>
        </div>
        <div className="relative lg:col-span-6">
          <FlowImage src={IMG} alt="A Hyderabad couple sharing chai beside their Walnut Pustak Rack" fade="soft" testid="story-image" className="aspect-[4/5] w-full rounded-[2rem]" />
          <img src="/brand/app-icon.png" alt="" className="absolute bottom-6 left-0 h-20 w-20 sm:h-24 sm:w-24" />
        </div>
      </section>
      <UspStrip />
    </div>
  );
}

const USPS = [
  [Truck, "24 Hrs", "Free delivery across Hyderabad", "Order today, build tomorrow. Our own team brings your flat-pack furniture to any door in Hyderabad and Secunderabad, lift or no lift."],
  [Wrench, "1 hex key", "Everything you need to build it", "DIY assembly with numbered parts, an illustrated guide and the hex key in the box. No drill, no tools to buy."],
  [Users, "0", "Carpenters needed", "Ready-to-assemble modular furniture made for two people and one evening. Skip the carpenter's visit and the wait."],
  [Layers, "7 shelves", "A modular bookshelf that grows", "The Pustak Rack holds books, plants and everything in between, and you can add a shelf anytime."],
];

const UspStrip = () => (
  <section data-testid="story-usp-strip" aria-labelledby="usp-heading" className="container-hw pb-16 lg:pb-24"><div className="grain relative overflow-hidden rounded-[2rem] bg-walnut text-linen">
    <PlankSymbol bar="currentColor" className="pointer-events-none absolute -right-24 -top-24 h-[420px] w-[420px] text-linen/[0.035]" />
    <div className="relative p-6 sm:p-10 lg:p-16">
      <div className="grid gap-6 lg:grid-cols-12 lg:items-end">
        <div className="lg:col-span-7">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brass">Why Hyderabad builds with H&amp;W</p>
          <h2 id="usp-heading" className="t-h2 mt-4 text-linen">DIY modular furniture without the hassle of a carpenter.</h2>
        </div>
        <p className="t-lead text-linen/70 lg:col-span-5">Flat-pack, ready-to-assemble furniture designed for Hyderabad flats: honest prices, simple builds, and modular pieces that grow with your home.</p>
      </div>
      <ul className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {USPS.map(([I, n, t, d], i) => (
          <li key={t} data-testid={`usp-item-${i}`} className={`rise d${i + 1} group relative overflow-hidden rounded-2xl border border-linen/10 bg-linen/5 p-7 transition-colors duration-300 hover:bg-linen/10`}>
            <span className="absolute inset-x-7 top-0 h-0.5 origin-left scale-x-0 bg-brass transition-transform duration-500 group-hover:scale-x-100" />
            <I className="h-6 w-6 text-brass" />
            <p className="price mt-8 text-4xl text-brass sm:text-5xl">{n}</p>
            <h3 className="t-h4 mt-3 text-linen">{t}</h3>
            <p className="t-small mt-3 text-linen/65">{d}</p>
          </li>
        ))}
      </ul>
      <div className="mt-12 flex flex-wrap items-center gap-4">
        <Link to="/product/pustak-rack" data-testid="usp-shop-button" className="btn-sheesham px-7 py-3.5">Shop the Pustak Rack</Link>
        <Link to="/#faq" className="text-sm font-semibold text-linen/80 underline-offset-4 hover:text-linen hover:underline">Read delivery &amp; build FAQs</Link>
      </div>
    </div>
    </div>
  </section>
);
